import { Position, WorkspaceElement, StaticEntry } from 'types';
import { Store } from 'stores/type';
import { WORKSPACE_ID, LIBRARY_ID, OUTSIDE_WORKSPACE_RATIO } from 'constants/drag';
import EventsManager from 'managers/events.manager';
import { intersects as checkIntersection } from 'helpers/drag';

const isStatic = (element: WorkspaceElement | StaticEntry): element is StaticEntry =>
  element.hasOwnProperty('dimensions');


class DropManager {
  private $store!: Store;
  private currentPosition: Position | null = null;
  private listening: WorkspaceElement[] = [];

  public init(store: Store) {
    this.$store = store;
  }

  public onDragStart() {
    if (!this.isMixingEnabled) return;

    this.listening = this.getSortedListening();
  }
  public onDragMove(position: Position) {
    this.currentPosition = position;

    if (this.checkStatic()) return;
    if (this.checkAlreadyOver()) return;
    if (!this.isMixingEnabled) return;

    const active = this.checkListening();
    if (active === null) return;
    if (this.isOver) return;

    this.triggerOver(active);
  }
  public onDragEnd(simulated: boolean | string = false) {
    this.currentPosition = null;

    if (!this.isOver) {
      if (this.isOutsideWorkspace()) {
        this.destroyDragged();
        return;
      }

      if (!this.wasCloned && simulated !== 'longpress') {
        EventsManager.emit('ELEMENT_DROP');
      }

      return;
    }

    if (this.shouldDestroyAfterDrop) {
      this.destroyDragged();
      return;
    }

    // if event was dispatched programatically then do not check if elements mix
    if (simulated) return;

    this.$store.dispatch('droppableDrop');
  }

  private destroyDragged() {
    const dragged = this.$store.getters.draggedId;
    if (dragged !== null) {
      this.$store.dispatch('workspaceElementRemove', dragged);
    }

    EventsManager.emit('ELEMENT_DESTROY');
  }

  private triggerOver({ id }: WorkspaceElement | StaticEntry) {
    this.$store.dispatch('dragOver', id);
  }
  private triggerOut() {
    this.$store.dispatch('dragOut');
  }

  private checkAlreadyOver() {
    if (!this.isOver) return false;

    const intersects = this.checkIntersection(this.over);
    if (!intersects) {
      this.triggerOut();
      return false;
    }

    return true;
  }
  private checkStatic() {
    const intersecting = this.static.find((droppable) => this.checkIntersection(droppable));
    if (intersecting) {
      if (!this.over || this.over.id !== intersecting.id) {
        this.triggerOver(intersecting);
      }

      return true;
    }

    return false;
  }
  private checkListening() {
    return this.listening.find((droppable) => this.checkIntersection(droppable)) || null;
  }

  private checkIntersection(element: WorkspaceElement | StaticEntry | null) {
    if (element === null) return false;
    const size = isStatic(element) ? element.dimensions : this.workspaceBoxDimensions;

    return checkIntersection(
      element.position,
      size,
      this.currentPosition!,
      this.workspaceBoxDimensions,
    );
  }

  private isOutsideWorkspace() {
    const dragged = this.$store.getters.draggedElement;
    const position = dragged ? dragged.position : null;
    if (!position) return false;

    const { x, y } = position;
    const { width, height } = this.workspaceBoxDimensions;

    if (x < -(width * OUTSIDE_WORKSPACE_RATIO)) return true;
    if (y < -(height * OUTSIDE_WORKSPACE_RATIO)) return true;
    if (y > window.innerHeight - (height * (1 - OUTSIDE_WORKSPACE_RATIO))) return true;

    return false;
  }

  private getSortedListening() {
    const result: WorkspaceElement[] = [];
    const droppables = this.$store.getters.droppablesListening;
    const domElements = document.querySelectorAll(`#${ WORKSPACE_ID } [data-id]`) as NodeListOf<HTMLElement>;
    Array.from(domElements).forEach(({ dataset: { id } }) => {
      const droppable = droppables.find((d) => d.id === id);
      if (!droppable) {
        return;
      }

      result.push(droppable);
    });

    return result.reverse();
  }

  get isMixingEnabled() {
    return !this.$store.getters.isMixingDisabled;
  }

  get static() {
    return Object.values(this.$store.getters.droppablesStatic);
  }

  get over() {
    return this.$store.getters.draggingOverElement;
  }
  get isOver() {
    return this.$store.getters.isOver;
  }

  get isDragging() {
    return this.$store.getters.isDragging;
  }
  get shouldDestroyAfterDrop() {
    return this.over && this.over.id === LIBRARY_ID;
  }

  get workspaceBoxDimensions() {
    return this.$store.getters.workspaceBoxDimensions;
  }

  // TODO: dirty hacking - make cloned indicator available globally
  // not only on cloned item instance
  get wasCloned() {
    const draggedElement = this.$store.getters.draggedElement;
    if (!draggedElement) return false;

    const element = document.querySelector(`[data-id="${ draggedElement.id }"]`) as any;
    if (!element || !element.__vue__) return false;

    return element.__vue__.cloned;
  }
}

export default new DropManager();
